export const OUTPUT_FPS = 30;
export const AUDIO_BITS_PER_SECOND = 96 * 1000;
export const MIN_VIDEO_BITS_PER_SECOND = 300 * 1000;
export const MAX_VIDEO_BITS_PER_SECOND = 4 * 1000 * 1000;
export const MAX_DURATION_SECONDS = 5 * 60;

const REFERENCE_PIXELS = 1280 * 720;
const REFERENCE_VIDEO_BITS_PER_SECOND = 1.5 * 1000 * 1000;
const DIMENSION_ALIGNMENT = 16;
const RESOLUTION_BITRATE_EXPONENT = 0.75;
const SOURCE_BITRATE_CEILING = 0.9;
const BITRATE_SAFETY = 0.88;
const RETRY_UNDERSHOOT = 0.85;
const WORTHWHILE_RATIO = 0.85;
const CONSTANT_QUALITY_QUANTIZER = 26;
const CONSTANT_QUALITY_OVERSHOOT = 1.25;

export function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

export function fitDimensions(sourceWidth, sourceHeight, maxDimension) {
  if (!sourceWidth || !sourceHeight) return null;

  const scale = maxDimension
    ? Math.min(1, maxDimension / Math.max(sourceWidth, sourceHeight))
    : 1;
  const align = (value) =>
    Math.max(DIMENSION_ALIGNMENT, Math.round(value / DIMENSION_ALIGNMENT) * DIMENSION_ALIGNMENT);

  return { width: align(sourceWidth * scale), height: align(sourceHeight * scale) };
}

export function planVideoEncode({ fileSize, duration, sourceWidth, sourceHeight, maxDimension, targetBytes }) {
  const size = fitDimensions(sourceWidth, sourceHeight, maxDimension);
  if (!size || !duration) return null;

  const outputPixels = size.width * size.height;
  const pixelRatio = clamp(outputPixels / (sourceWidth * sourceHeight), 0, 1);

  const qualityBitsPerSecond =
    REFERENCE_VIDEO_BITS_PER_SECOND * Math.pow(outputPixels / REFERENCE_PIXELS, RESOLUTION_BITRATE_EXPONENT);
  const sourceVideoBitsPerSecond = Math.max(0, (fileSize * 8) / duration - AUDIO_BITS_PER_SECOND);
  const sourceCeiling =
    sourceVideoBitsPerSecond * Math.pow(pixelRatio, RESOLUTION_BITRATE_EXPONENT) * SOURCE_BITRATE_CEILING;
  const budgetBitsPerSecond = (targetBytes * 8 * BITRATE_SAFETY) / duration - AUDIO_BITS_PER_SECOND;

  const videoBitsPerSecond = clamp(
    Math.min(qualityBitsPerSecond, sourceCeiling, budgetBitsPerSecond),
    MIN_VIDEO_BITS_PER_SECOND,
    MAX_VIDEO_BITS_PER_SECOND
  );
  const projectedBytes = ((videoBitsPerSecond + AUDIO_BITS_PER_SECOND) * duration) / 8;

  return {
    size,
    videoBitsPerSecond,
    projectedBytes,
    worthwhile: fileSize > targetBytes || projectedBytes <= fileSize * WORTHWHILE_RATIO,
  };
}

export function retryBitrate(bitrate, producedBytes, targetBytes) {
  return clamp(
    bitrate * (targetBytes / producedBytes) * RETRY_UNDERSHOOT,
    MIN_VIDEO_BITS_PER_SECOND,
    MAX_VIDEO_BITS_PER_SECOND
  );
}

function createFrameCanvas(width, height) {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(width, height);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

async function hasVideoTrack(mediabunny, blob) {
  const { ALL_FORMATS, BlobSource, Input } = mediabunny;
  const input = new Input({ source: new BlobSource(blob), formats: ALL_FORMATS });
  try {
    const track = await input.getPrimaryVideoTrack();
    return Boolean(track && track.displayWidth > 0 && track.displayHeight > 0);
  } catch {
    return false;
  } finally {
    input.dispose();
  }
}

export async function transcodeVideo(file, { targetBytes, maxDimension, onProgress, onCancelable, onPlan } = {}) {
  const mediabunny = await import('mediabunny');
  const {
    ALL_FORMATS, BlobSource, BufferTarget, Conversion, Input,
    Mp4OutputFormat, Output, Quality, WebMOutputFormat,
    getFirstEncodableVideoCodec, getFirstEncodableAudioCodec,
  } = mediabunny;

  let plan;
  let duration;
  let sourceHasAudio = false;
  const probe = new Input({ source: new BlobSource(file), formats: ALL_FORMATS });
  try {
    const videoTrack = await probe.getPrimaryVideoTrack();
    if (!videoTrack || !(await videoTrack.canDecode())) return { reason: 'undecodable' };
    sourceHasAudio = Boolean(await probe.getPrimaryAudioTrack());

    duration = await probe.computeDuration();
    if (!Number.isFinite(duration) || duration <= 0) return { reason: 'undecodable' };
    if (duration > MAX_DURATION_SECONDS) return { reason: 'too-long' };

    plan = planVideoEncode({
      fileSize: file.size,
      duration,
      sourceWidth: videoTrack.displayWidth,
      sourceHeight: videoTrack.displayHeight,
      maxDimension,
      targetBytes,
    });
    if (!plan) return { reason: 'undecodable' };
    if (!plan.worthwhile) return { reason: 'already-efficient' };
    onPlan?.({ duration, ...plan });
  } finally {
    probe.dispose();
  }

  const { size } = plan;
  const videoCodec = await getFirstEncodableVideoCodec(['avc', 'vp9', 'vp8'], {
    width: size.width,
    height: size.height,
  });
  if (!videoCodec) return { reason: 'no-encoder' };

  const wantsMp4 = videoCodec === 'avc';
  const audioCodec = await getFirstEncodableAudioCodec(wantsMp4 ? ['aac'] : ['opus']);
  const mimeType = wantsMp4 ? 'video/mp4' : 'video/webm';
  const copiesSourceAudio = !audioCodec && sourceHasAudio;
  const audioOptions = audioCodec
    ? { codec: audioCodec, quality: new Quality({ bitrate: AUDIO_BITS_PER_SECOND }) }
    : copiesSourceAudio
      ? {}
      : { discard: true };

  const runPass = async (quality) => {
    const input = new Input({ source: new BlobSource(file), formats: ALL_FORMATS });
    const output = new Output({
      format: wantsMp4 ? new Mp4OutputFormat({ fastStart: 'in-memory' }) : new WebMOutputFormat(),
      target: new BufferTarget(),
    });

    const frameCanvas = createFrameCanvas(size.width, size.height);
    const frameContext = frameCanvas.getContext('2d', { alpha: false });

    try {
      const conversion = await Conversion.init({
        input,
        output,
        video: {
          codec: videoCodec,
          frameRate: OUTPUT_FPS,
          quality,
          allowRotationMetadata: false,
          processedWidth: size.width,
          processedHeight: size.height,
          process: (sample) => {
            sample.drawWithFit(frameContext, { fit: 'fill' });
            return frameCanvas;
          },
        },
        audio: audioOptions,
        showWarnings: false,
      });

      const lostAudio =
        copiesSourceAudio && conversion.discardedTracks.some((entry) => entry.track.isAudioTrack());
      if (!conversion.isValid || lostAudio) return null;

      onCancelable?.(() => conversion.cancel());
      if (onProgress) {
        conversion.onProgress = (progress) => onProgress(clamp(progress, 0, 1));
      }
      await conversion.execute();

      const buffer = output.target.buffer;
      return buffer ? new Blob([buffer], { type: mimeType }) : null;
    } finally {
      onCancelable?.(null);
      input.dispose();
    }
  };

  const bitrate = plan.videoBitsPerSecond;
  let blob = await runPass(
    new Quality({ quantizer: CONSTANT_QUALITY_QUANTIZER, bitrate: Math.floor(bitrate) })
  );
  if (!blob) return { reason: 'unsupported' };

  if (blob.size > Math.min(targetBytes, plan.projectedBytes * CONSTANT_QUALITY_OVERSHOOT)) {
    onProgress?.(0);
    const capped = await runPass(new Quality({ bitrate: Math.floor(bitrate) }));
    if (capped && capped.size > 0 && capped.size < blob.size) blob = capped;
  }

  if (blob.size > targetBytes) {
    const corrected = retryBitrate(bitrate, blob.size, targetBytes);
    if (corrected < bitrate) {
      onProgress?.(0);
      const retried = await runPass(new Quality({ bitrate: Math.floor(corrected) }));
      if (retried && retried.size > 0 && retried.size < blob.size) blob = retried;
    }
  }

  if (!blob.size || blob.size >= file.size) return { reason: 'no-saving' };
  if (!(await hasVideoTrack(mediabunny, blob))) return { reason: 'unsupported' };

  onProgress?.(1);
  return {
    blob,
    mimeType,
    stats: {
      width: size.width,
      height: size.height,
      videoBitsPerSecond: Math.round(bitrate),
      codec: videoCodec,
    },
  };
}
